import React from 'react';
import { PieDatum, ResponsivePie } from '@nivo/pie';
import { chartColors, performanceIndicatorColors, performanceIndicatorDefinitions, PerformanceIndicatorLevel, themeColors } from '../../../constants';
import {
  makeStyles,
  Theme,
  createStyles,
  Typography,
  Button,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { IChartProps, IReduxState } from '../../../types';
import { CardLayout } from '../CardLayouts/';
import { PerformanceIndicatorDto } from '../../../rubblemaster_openapi3_typescript-fetch';
import { FormatHelper } from '../../../helpers';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    lastUpdate: {
      position: 'absolute',
      left: theme.spacing(1),
      top: 0,
      color: themeColors.white,
    },
    infoCircle: {
      position: 'absolute',
      right: theme.spacing(1),
      top: 0,

      minWidth: 0,
      width: 0,
      height: 0,
      
      color: chartColors.rmYellow,
      border: `1px solid ${chartColors.rmYellow}`,
      borderRadius: '2rem',
      padding: '1rem',

      display: 'flex',
      alignItems: 'center',
      justifyItems: 'center'
    },
    infoCircleText: {
      textTransform: 'lowercase'
    },
    infoTooltipContainer: {
      position: 'absolute',
      right: 0,
      overflow: 'visible',
      margin: '10px'
    },
    infoTooltip: {
      border: '1px solid black',
      borderRadius: '2rem',
      padding: '1rem',
      
      color: themeColors.white,
      background: theme.palette.background.paper
    },
    title: {
      marginTop: theme.spacing(2),
    },
    gaugesWrapper: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(3),
    },
    gaugeWrapper: {
      height: 230,
      width: 230,
    },
    noData: {
      height: 100,
      width: 200,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  })
);

function *mapPerformanceData(
  t: (key: string) => string, 
  performanceIndicators: PerformanceIndicatorDto
): Iterable<PieDatum> {
  for (var id of Object.keys(performanceIndicatorColors)) {
    const level = id as PerformanceIndicatorLevel;
    const value = performanceIndicators[level] ?? 0;
    const colors = performanceIndicatorColors[level];
    
    yield {
      id,
      label: (value * 100).toFixed(2) + '% ' + t(`graph.performanceIndicatorLevels.${id}`),
      value,
      sliceColor: colors.slice,
      labelColor: colors.label,
    };
  }
}

export default function PerformanceIndicatorGraphGauge(props: IChartProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  const performanceIndicators = useSelector(
    (state: IReduxState) => state.machineDetails.performanceIndicators
  );
  const performanceData = [...mapPerformanceData(t, performanceIndicators)];
  const performanceSlices = performanceData.filter(d => d.value > 0.01);

  const isHybrid = useSelector(
    (state: IReduxState) =>
      // !!state.machineDetails.machine.telemetrySnapshot?.isHybrid
      !!state.machineDetails.machine.isHybrid
  );

  const [tooltipVisible, setTooltipVisibility] = React.useState(false);
  const toggleTooltip = () => {
    setTooltipVisibility(visible => !visible);
  };

  const tooltip = props.isPdfReport
    ? undefined
    : (<React.Fragment>
        <Button className={classes.infoCircle} onClick={toggleTooltip}>
          <Typography className={classes.infoCircleText}>
            i
          </Typography>
        </Button>
        
        {tooltipVisible &&
          <div className={classes.infoTooltipContainer}>
            <div className={classes.infoTooltip}>
              <p>
                {t('graph.performanceIndicatorTooltip.description')}
              </p>

              <p>
                {t('graph.performanceIndicatorTooltip.thresholds')}:
              </p>

              <ul>
                {Object.keys(performanceIndicatorDefinitions).map((key) => {
                  const level = key as PerformanceIndicatorLevel;
                  const definition = performanceIndicatorDefinitions[level];
                  const color = performanceIndicatorColors[level].label;

                  return (
                    <li key={`graph.performanceIndicatorLevels.${level}`}>
                      <span>
                        {t('graph.performanceIndicatorTooltip.engineLoad')} {definition}
                      </span>
                      &nbsp;=&nbsp;
                      <span style={{ color }}>
                        {t(`graph.performanceIndicatorLevels.${level}`)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>}
      </React.Fragment>);
  
  return (
    <CardLayout
      cardStyle={{
        boxShadow: isHybrid
          ? `0.25rem 0.25rem 0 0 ${chartColors.electricBlue}`
          : 'none',
      }}
    >
      <Typography className={classes.lastUpdate} variant="caption">
        {`${t('graph.lastReceivedValue')}: ${FormatHelper.formatTimestamp(performanceIndicators.timestamp)}`}
      </Typography>

      <Typography
        className={classes.title}
        align="center"
        variant="h5"
      >
        {t('graph.performanceIndicator')}
      </Typography>

      {tooltip}

      {performanceSlices?.length > 0 ? (
        <React.Fragment>
          <div className={classes.gaugesWrapper}>
            <div className={classes.gaugeWrapper}>
              <ResponsivePie
                data={performanceSlices}
                enableRadialLabels={false}
                enableSlicesLabels={false}
                isInteractive={false}
                innerRadius={0.6}
                padAngle={1}
                cornerRadius={3}
                colors={(d: any) => d.sliceColor}
                borderColor={{
                  from: 'color',
                  modifiers: [['darker', 0.6]],
                }}
                animate={true}
                motionStiffness={90}
                motionDamping={15}
                margin={{ top: 5, bottom: 5 }} 
              />
            </div>
          </div>

          {performanceData.reverse().map(d =>
            <Typography
              align="center"
              variant="body1"
              key={d.id}
              style={{ color: d['labelColor'] as string }}
            >
              {d['label']}
            </Typography>
          )}
        </React.Fragment>
      ) : (
        <div className={classes.noData}>
          <Typography
            className={classes.title}
            align="center"
            variant="h6"
          >
            {t('graph.noData')}
          </Typography>
        </div>
      )}
    </CardLayout>
  );
}
